/**
 * ComingSoonPage — the Invite page's "Coming soon" invitation card, shared by the
 * client pages that announce a feature still to come (Invite, Waitlists; web-account-
 * pages D10). A ClientShell child: it renders its OWN header (shipped glass back
 * square → Home + title) into the shell header slot, and a display-only body in
 * the shell's scrolling <main>. NO interactive elements in the body. All styling
 * is via injected theme tokens.
 *
 * `ComingSoonCard` is the body alone, for a surface that has no ClientShell.
 *
 * Visual authority: docs/mockups/client-dashboard-redesign/invite-coming-soon.html
 * (Frame 3 "The invitation", confirmed 2026-07-05).
 */
import { useCallback, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { withRecipientToken } from '@/attribution';
import { useClientShellContext } from '@/components/client/ClientShell/ClientShellContext';
import { FlowBackButton } from '@/components/booking/FlowBackButton';
import styles from './InvitePage.module.css';

export interface ComingSoonCopy {
  kicker: string;
  /** The headline's two lines. */
  title: [string, string];
  body: string;
  foot: string;
}

/** Accent diamond glyph for the invitation divider. */
function DiamondIcon() {
  return (
    <svg viewBox="0 0 10 10" fill="currentColor" aria-hidden="true">
      <rect x="5" y="0" width="7" height="7" transform="rotate(45 5 5)" rx="1" />
    </svg>
  );
}

export function ComingSoonHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className={styles.pageHeaderSection}>
      <FlowBackButton className={styles.back} onClick={onBack} />
      <div className={styles.pageTitleBlock}>
        <h1 className={styles.pageTitle}>{title}</h1>
      </div>
    </div>
  );
}

export function ComingSoonCard({ copy, testId }: { copy: ComingSoonCopy; testId: string }) {
  return (
    <div className={styles.csWrap} data-testid={testId}>
      <section className={`${styles.gcard} ${styles.invitation}`}>
        <div className={styles.invitationInner} aria-hidden="true" />
        <div className={styles.invKicker}>{copy.kicker}</div>
        <h2 className={styles.invTitle}>
          {copy.title[0]}
          <br />
          {copy.title[1]}
        </h2>
        <div className={styles.invDivider} aria-hidden="true">
          <DiamondIcon />
        </div>
        <p className={styles.invBody}>{copy.body}</p>
        <div className={styles.invFoot}>{copy.foot}</div>
      </section>
    </div>
  );
}

export function ComingSoonPage({
  headerTitle,
  testId,
  copy,
}: {
  headerTitle: string;
  testId: string;
  /** The card's words; the body names the studio. */
  copy: (businessName: string) => ComingSoonCopy;
}) {
  const navigate = useNavigate();
  const { dashboard, setHeaderSlot } = useClientShellContext();

  const handleBack = useCallback(() => navigate(withRecipientToken('/booking-hub')), [navigate]);

  // Render the page header into the shell's (non-scrolling) header slot.
  useLayoutEffect(() => {
    setHeaderSlot(<ComingSoonHeader title={headerTitle} onBack={handleBack} />);
    return () => setHeaderSlot(null);
  }, [setHeaderSlot, handleBack, headerTitle]);

  return <ComingSoonCard copy={copy(dashboard.business.name)} testId={testId} />;
}
